import { Divider, Input, SizableText, XStack, YStack } from '@mythical/ui'
import { Button } from 'app/components/primitive/Button'
import config from 'app/config'
import makeSiteGraphqlRequest from 'app/lib/graphql-request'
import {
  newsletterSubscribeMutation,
  newsletterSubscribeResult,
} from 'app/mutations/newsletter/subscribe'
import { useState } from 'react'

const NewsletterSignup = () => {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle')
  const [message, setMessage] = useState<string | null>(null)

  const handleSubscribe = async () => {
    const trimmed = email.trim().toLowerCase()

    if (!trimmed) {
      setStatus('error')
      setMessage('Please enter your email.')
      return
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setStatus('error')
      setMessage('Please enter a valid email.')
      return
    }

    setStatus('loading')
    setMessage(null)

    try {
      const data = await makeSiteGraphqlRequest(
        config,
        newsletterSubscribeMutation(),
        { email: trimmed },
        {
          formatFn: newsletterSubscribeResult,
        },
      )

      if (data?.success) {
        setStatus('success')
        setMessage("Thanks for subscribing! You'll hear from us soon.")
        setEmail('')
      } else {
        setStatus('error')
        setMessage(data?.reason || 'Subscription failed. Please try again.')
      }
    } catch (error) {
      console.error(error)
      setStatus('error')
      setMessage('Subscription failed. Please try again.')
    }
  }

  return (
    <YStack
      gap="$12"
      maw={1440}
      mx="auto"
      p="$5"
      w="100%"
      $gtMd={{ gap: '$14' }}
    >
      <Divider boc="$brandPink" />

      <YStack gap="$9" $gtMd={{ gap: '$14' }}>
        <SizableText
          col="$brandPink"
          fontFamily="$narrow"
          size="$12"
          fow="800"
          tt="uppercase"
          $gtMd={{ size: '$15' }}
        >
          Just slightly{'\n'}curious?
        </SizableText>

        <XStack
          ai="flex-start"
          fd="column"
          fw="wrap"
          gap="$9"
          $gtMd={{ ai: 'flex-end', fd: 'row', jc: 'space-between' }}
        >
          <SizableText
            col="$brandPink"
            f={1}
            maw={562}
            size="$4"
            fow="500"
            $gtMd={{ size: '$5' }}
          >
            Get the latest updates and discounts straight to your inbox -- join
            when you’re ready!
          </SizableText>

          <XStack gap="$3" maw={693} w="100%" f={1}>
            <Input
              size="$4"
              bg="transparent"
              bw={0}
              bbw={2}
              f={1}
              boc="$brandPink"
              col="$brandPink"
              br="$0"
              px="$0"
              fow="500"
              focusStyle={{
                bbw: 2,
                boc: '$brandPink',
                outlineStyle: 'none',
              }}
              hoverStyle={{
                bbw: 2,
                boc: '$brandPink',
              }}
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email"
              placeholderTextColor="$brandPink"
              focusVisibleStyle={{ outlineWidth: 0 }}
              $gtMd={{
                size: '$6',
                br: 0,
                px: '$0',
              }}
            />
            <Button
              fontWeight="500"
              onPress={handleSubscribe}
              theme="pink"
              disabled={status === 'loading'}
              $gtMd={{ size: '$6' }}
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </XStack>
          {message ? (
            <SizableText
              col={status === 'error' ? '$red10' : '$brandPink'}
              size="$3"
              fow="500"
            >
              {message}
            </SizableText>
          ) : null}
        </XStack>
      </YStack>

      <Divider boc="$brandPink" />
    </YStack>
  )
}

export default NewsletterSignup
